import React, { useCallback, useEffect, useState } from "react";
import NavHeader from "../nav/NavHeader";
import SideMenu from "../nav/Sidemenu";
import RoleManagementTable from "../rolesRegistrations/RoleManagementTable";
import RoleRegistrationsForm from "../rolesRegistrations/RoleRegistrationsForm";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import "../../assets/css/reportRegistrations.css";

const RolesAdministration = () => {
    const [activeTab, setActiveTab] = useState("management");
    const [searchText, setSearchText] = useState("");
    const [roles, setRoles] = useState([]);
    const [isRolesLoading, setIsRolesLoading] = useState(true);
    const [rolesError, setRolesError] = useState("");
    const [showEditModal, setShowEditModal] = useState(false);
    const [editRoleData, setEditRoleData] = useState(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleteRoleData, setDeleteRoleData] = useState(null);
    const [isUpdatingRole, setIsUpdatingRole] = useState(false);
    const [deletingRoleId, setDeletingRoleId] = useState(null);
    const [toastState, setToastState] = useState({
        show: false,
        type: "success",
        message: "",
    });

    const passtoken =
        !localStorage.getItem("access-token-storage") ||
        localStorage.getItem("access-token-storage") === null
            ? null
            : JSON.parse(localStorage.getItem("access-token-storage"))["accessToken"];

    const rolesEndpoint = `${process.env.REACT_APP_SERVER_URL}/aipd/role-management/roles`;

    const showToast = (message, type = "success") => {
        setToastState({
            show: true,
            type,
            message,
        });
    };

    const formatNow = () => {
        const now = new Date();
        const pad = (value) => String(value).padStart(2, "0");
        return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(
            now.getDate()
        )} ${pad(now.getHours())}:${pad(now.getMinutes())}`;
    };

    const normalizeRolesPayload = (payload) => {
        if (Array.isArray(payload)) {
            return payload;
        }

        if (Array.isArray(payload?.data?.roles)) {
            return payload.data.roles;
        }

        if (payload?.data && Array.isArray(payload.data)) {
            return payload.data;
        }

        if (payload?.data && typeof payload.data === "object") {
            return [payload.data];
        }

        if (Array.isArray(payload?.roles)) {
            return payload.roles;
        }

        if (payload && typeof payload === "object" && (payload.roleId || payload.role_id)) {
            return [payload];
        }

        return [];
    };

    const formatRoleCreatedAt = (createdAt) => {
        if (createdAt === null || createdAt === undefined || createdAt === "") {
            return "-";
        }

        if (typeof createdAt === "number") {
            const date = new Date(createdAt);
            return Number.isNaN(date.getTime()) ? String(createdAt) : date.toLocaleString();
        }

        return String(createdAt);
    };

    const fetchRoles = useCallback(async () => {
        setIsRolesLoading(true);
        setRolesError("");

        try {
            const headers = {
                Accept: "application/json",
            };

            if (passtoken) {
                headers["oidc-token"] = passtoken;
            }

            const response = await fetch(rolesEndpoint, { headers });
            const json = await response.json();

            if (!response.ok) {
                throw new Error(json?.message || "Failed to load roles.");
            }

            const fetchedRoles = normalizeRolesPayload(json).map((role) => ({
                id: role.roleId ?? role.role_id ?? role.id,
                name: role.roleName || role.role_name || role.name || "-",
                dealerCode: role.dealerCode ?? role.dealer_code ?? "-",
                description: role.roleDesc || role.roleDescription || "",
                createdBy: role.createdBy || role.created_by || role.createdby || "-",
                createdAt: formatRoleCreatedAt(role.createdAt ?? role.created_at),
            }));

            setRoles(fetchedRoles);
        } catch (error) {
            setRoles([]);
            setRolesError(error?.message || "Unable to fetch roles at the moment.");
        } finally {
            setIsRolesLoading(false);
        }
    }, [passtoken, rolesEndpoint]);

    useEffect(() => {
        fetchRoles();
    }, [fetchRoles]);

    useEffect(() => {
        if (!toastState.show) {
            return undefined;
        }

        const timer = setTimeout(() => {
            setToastState((prevState) => ({ ...prevState, show: false }));
        }, 3000);

        return () => clearTimeout(timer);
    }, [toastState.show]);

    const filteredRoles = roles.filter((role) =>
        String(role.name || "").toLowerCase().includes(searchText.toLowerCase())
    );

    const handleCreateRole = (name, dealerCode) => {
        const isDuplicate = roles.some(
            (role) => role.name.toLowerCase() === name.toLowerCase()
        );

        if (isDuplicate) {
            return;
        }

        const createdBy =
            localStorage.getItem("userName") || localStorage.getItem("myemail") || "Current User";

        setRoles((prevData) => [
            ...prevData,
            {
                id: Date.now(),
                name,
                dealerCode: String(dealerCode || "").trim() || "-",
                createdBy,
                createdAt: formatNow(),
            },
        ]);
        setActiveTab("management");
    };

    const handleOpenDeleteModal = (role) => {
        if (!role || deletingRoleId !== null) {
            return;
        }

        setDeleteRoleData({
            roleId: role.id,
            roleName: role.name,
        });
        setShowDeleteModal(true);
    };

    const handleCloseDeleteModal = () => {
        if (deletingRoleId !== null) {
            return;
        }

        setShowDeleteModal(false);
        setDeleteRoleData(null);
    };

    const handleDeleteRole = async () => {
        const roleId = deleteRoleData?.roleId;

        if (!roleId || deletingRoleId !== null) {
            return;
        }

        setDeletingRoleId(roleId);

        try {
            const headers = {
                Accept: "application/json",
            };

            if (passtoken) {
                headers["oidc-token"] = passtoken;
            }

            const response = await fetch(`${rolesEndpoint}/${roleId}`, {
                method: "DELETE",
                headers,
            });

            if (response.status === 204) {
                setRoles((prevData) => prevData.filter((role) => role.id !== roleId));
                showToast("Role deleted successfully.", "success");
                setShowDeleteModal(false);
                setDeleteRoleData(null);
                return;
            }

            if (response.status === 404) {
                throw new Error("Role not found.");
            }

            const responseJson = await response.json().catch(() => null);

            if (!response.ok) {
                throw new Error(responseJson?.message || "Failed to delete role.");
            }

            setRoles((prevData) => prevData.filter((role) => role.id !== roleId));
            showToast("Role deleted successfully.", "success");
            setShowDeleteModal(false);
            setDeleteRoleData(null);
        } catch (error) {
            showToast(
                error?.message || "Unable to delete role at the moment.",
                "danger"
            );
        } finally {
            setDeletingRoleId(null);
        }
    };

    const handleEditRole = (role) => {
        setEditRoleData(role);
        setShowEditModal(true);
    };

    const handleEditChange = (event) => {
        const { name, value } = event.target;
        setEditRoleData((prevData) => ({ ...prevData, [name]: value }));
    };

    const handleCloseEditModal = () => {
        setShowEditModal(false);
        setEditRoleData(null);
    };

    const handleUpdateRole = (event) => {
        event.preventDefault();

        if (!editRoleData) {
            return;
        }

        const roleName = String(editRoleData.name || "").trim();
        const dealerCode = String(editRoleData.dealerCode || "").trim();

        if (!roleName) {
            showToast("Role Name is required.", "danger");
            return;
        }

        const updateRole = async () => {
            setIsUpdatingRole(true);

            try {
                const headers = {
                    Accept: "application/json",
                    "Content-Type": "application/json",
                };

                if (passtoken) {
                    headers["oidc-token"] = passtoken;
                }

                const response = await fetch(`${rolesEndpoint}/${editRoleData.id}`, {
                    method: "PUT",
                    headers,
                    body: JSON.stringify({ roleName, dealerCode }),
                });

                const responseJson = await response.json().catch(() => null);

                if (!response.ok) {
                    throw new Error(responseJson?.message || "Failed to update role.");
                }

                setRoles((prevData) =>
                    prevData.map((role) =>
                        role.id === editRoleData.id
                            ? { ...role, name: roleName, dealerCode: dealerCode || "-" }
                            : role
                    )
                );

                showToast("Role updated successfully.", "success");
                handleCloseEditModal();
            } catch (error) {
                showToast(
                    error?.message || "Unable to update role at the moment.",
                    "danger"
                );
            } finally {
                setIsUpdatingRole(false);
            }
        };

        updateRole();
    };

    return (
        <div>
            {toastState.show && (
                <div
                    style={{
                        position: "fixed",
                        top: "16px",
                        right: "16px",
                        zIndex: 99999,
                        minWidth: "320px",
                        maxWidth: "420px",
                    }}
                >
                    <div
                        className={`alert alert-${toastState.type} shadow-sm mb-0`}
                        role="alert"
                    >
                        {toastState.message}
                    </div>
                </div>
            )}

            <NavHeader />
            <SideMenu />
            <div style={{ position: "relative" }} id="main">
                <div className="container-fluid pt-3 pb-4">
                    <h1 className="mb-1">Role Administration</h1>
                    <p className="text-muted mb-0">Manage and register roles for your application.</p>
                </div>

                <div className="container-fluid pb-4">
                    <div className="rr-tabsWrap">
                        <button
                            type="button"
                            onClick={() => setActiveTab("management")}
                            className={`rr-tabBtn ${activeTab === "management" ? "active" : ""}`}
                        >
                            Role Management
                        </button>
                        <button
                            type="button"
                            onClick={() => setActiveTab("registrations")}
                            className={`rr-tabBtn ${activeTab === "registrations" ? "active" : ""}`}
                        >
                            Role Registration
                        </button>
                    </div>
                </div>

                {activeTab === "management" ? (
                    <RoleManagementTable
                        searchText={searchText}
                        setSearchText={setSearchText}
                        filteredRoles={filteredRoles}
                        isLoading={isRolesLoading}
                        errorMessage={rolesError}
                        deletingRoleId={deletingRoleId}
                        onEditRole={handleEditRole}
                        onDeleteRole={handleOpenDeleteModal}
                    />
                ) : (
                    <RoleRegistrationsForm onCreateRole={handleCreateRole} />
                )}

                <Dialog
                    open={Boolean(showEditModal && editRoleData)}
                    onClose={handleCloseEditModal}
                    fullWidth
                    maxWidth="sm"
                    PaperProps={{ className: "rr-manageCard rr-editDialogPaper" }}
                >
                    <DialogTitle disableTypography className="border-0 rr-deleteModalHeader rr-deleteModalHeaderFlex">
                        <h5 className="modal-title mb-0">Edit Role</h5>
                        <button
                            type="button"
                            className="btn-close btn-close-white"
                            aria-label="Close"
                            onClick={handleCloseEditModal}
                        ></button>
                    </DialogTitle>

                    <form onSubmit={handleUpdateRole}>
                        <DialogContent className="pt-3">
                            <div className="mb-3">
                                <label className="form-label rr-label">Role Name</label>
                                <input
                                    type="text"
                                    name="name"
                                    className="form-control rr-input"
                                    value={editRoleData?.name || ""}
                                    onChange={handleEditChange}
                                    required
                                />
                            </div>
                            <div>
                                <label className="form-label rr-label">Dealer Code</label>
                                <input
                                    type="text"
                                    name="dealerCode"
                                    className="form-control rr-input"
                                    value={editRoleData?.dealerCode === "-" ? "" : editRoleData?.dealerCode || ""}
                                    onChange={handleEditChange}
                                />
                            </div>
                        </DialogContent>

                        <DialogActions className="border-0 pt-2">
                            <button
                                type="button"
                                className="btn rr-btnGhost rr-deleteModalActionBtn"
                                onClick={handleCloseEditModal}
                                disabled={isUpdatingRole}
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                className="btn rr-btnPrimary rr-deleteModalActionBtn"
                                disabled={isUpdatingRole}
                            >
                                {isUpdatingRole ? (
                                    <>
                                        <span
                                            className="spinner-border spinner-border-sm me-2"
                                            role="status"
                                            aria-hidden="true"
                                        ></span>
                                        Updating...
                                    </>
                                ) : (
                                    "Update"
                                )}
                            </button>
                        </DialogActions>
                    </form>
                </Dialog>

                <Dialog
                    open={Boolean(showDeleteModal && deleteRoleData)}
                    onClose={handleCloseDeleteModal}
                    fullWidth
                    maxWidth="sm"
                    PaperProps={{ className: "rr-manageCard rr-deleteDialogPaper" }}
                >
                    <DialogTitle disableTypography className="border-0 rr-deleteModalHeader rr-deleteModalHeaderFlex">
                        <h5 className="modal-title mb-0">Confirm Deletion</h5>
                        <button
                            type="button"
                            className="btn-close btn-close-white"
                            aria-label="Close"
                            onClick={handleCloseDeleteModal}
                            disabled={deletingRoleId !== null}
                        ></button>
                    </DialogTitle>

                    <DialogContent className="pt-3">
                        <p className="mb-0 rr-deleteModalText">
                            Are you sure you want to delete role
                            {" "}
                            <strong>{deleteRoleData?.roleName || "this role"}</strong>
                            ? This action cannot be undone.
                        </p>
                    </DialogContent>

                    <DialogActions className="border-0 pt-2">
                        <button
                            type="button"
                            className="btn rr-btnGhost rr-deleteModalActionBtn"
                            onClick={handleCloseDeleteModal}
                            disabled={deletingRoleId !== null}
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            className="btn rr-btnPrimary rr-deleteModalActionBtn"
                            onClick={handleDeleteRole}
                            disabled={deletingRoleId !== null}
                        >
                            {String(deletingRoleId) === String(deleteRoleData?.roleId) ? (
                                <>
                                    <span
                                        className="spinner-border spinner-border-sm me-2"
                                        role="status"
                                        aria-hidden="true"
                                    ></span>
                                    Deleting...
                                </>
                            ) : (
                                "Delete"
                            )}
                        </button>
                    </DialogActions>
                </Dialog>
            </div>
        </div>
    );
};

export default RolesAdministration;
